import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Loader2, FileText, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HouseAgentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workOrder, setWorkOrder] = useState(null);
  const [error, setError] = useState(null);

  const workOrderId = searchParams.get('order');

  useEffect(() => {
    const checkStatus = async () => {
      if (!workOrderId) {
        setError('No work order ID provided');
        setLoading(false);
        return;
      }

      try {
        // Poll for completion
        const checkWork = async () => {
          const response = await axios.get(`${API}/house-agents/work-order/${workOrderId}`);
          setWorkOrder(response.data);
          
          if (response.data.status === 'completed') {
            setLoading(false);
          } else if (response.data.status === 'failed') {
            setError('Work processing failed. Please contact support.');
            setLoading(false);
          } else if (response.data.status === 'pending_payment' || response.data.status === 'paid') {
            // Try to verify payment and trigger work
            try {
              await axios.post(`${API}/house-agents/verify-and-complete/${workOrderId}`);
            } catch (e) {
              // Ignore errors, keep polling
            }
            setTimeout(checkWork, 3000);
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkWork, 2000);
          }
        };
        
        await checkWork();
      } catch (err) {
        setError('Failed to fetch work order status');
        setLoading(false);
      }
    };

    checkStatus();
  }, [workOrderId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">Processing Your Work...</h1>
          <p className="text-zinc-400">Our AI agent is working on your request</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Error</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/agents')} variant="outline">
            Browse Agents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Work Completed!</h1>
          <p className="text-zinc-400">
            {workOrder?.agent_name} has finished your task
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Delivered Work</h2>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-4 max-h-96 overflow-y-auto">
            <pre className="text-zinc-300 whitespace-pre-wrap text-sm leading-relaxed">
              {workOrder?.work_output}
            </pre>
          </div>
        </motion.div>

        <div className="flex gap-4 justify-center">
          <Button
            onClick={() => {
              navigator.clipboard.writeText(workOrder?.work_output || '');
            }}
            variant="outline"
            className="border-white/20"
          >
            Copy to Clipboard
          </Button>
          <Button
            onClick={() => navigate('/agents')}
            className="bg-cyan-500 hover:bg-cyan-600"
          >
            Hire Another Agent
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
